/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR097: Hook for fetching admin live preview deals
 * CR437: Hydrates admin/live deals through bulk render payload RPC (SSOT contract)
 * 
 * Supports three modes:
 *   - staging: Fetch from cr075_staging_deals_deduped for a specific batch_run_id
 *   - admin: Fetch all production deals (unfiltered internal view)
 *   - live: Fetch production deals filtered by site_state pointer (public view)
 * 
 * NOTE: This does NOT query legacy tables (test_run_deals, test_runs, etc.)
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { type Deal, type DealCategory } from "@/lib/types/deals";
import { hydrateFlyerArtifactsBatch } from "@/lib/deals/fetchFlyerSnippetForDeal";
import { lookupByProgramName, calculatePointsCashValue } from "@/lib/deals/loyaltyProgramLookup";

export type PreviewMode = "staging" | "admin" | "live";

// CR332: Flyer artifact from deal_artifacts table
export interface FlyerArtifact {
  artifact_type: 'flyer_snippet';
  page_number: number | null;
  page_image_path: string | null;
  snippet_asset_path: string | null;
  // CR416: Durable thumbnail from flyer_scan_pages_v2
  thumbnail_storage_path?: string | null;
  bbox_norm?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  bbox_px?: {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null;
}

interface AdminPreviewDealResponse {
  id: string;
  merchant_name: string;
  deal_type: string;
  deal_subtype: string | null;
  headline_mechanic: string | null;
  purchase_gift_card_brand: string | null;
  nested_purchase_brand?: string | null;
  qualifying_product_name?: string | null;
  bonus_merchant: string | null;
  source_type: string | null;
  source_url: string | null;
  claim_url: string | null;
  valid_from: string | null;
  valid_until: string | null;
  availability_scope: string | null;
  availability_notes: string | null;
  computed_value_cad: number | null;
  effective_percent_back: number | null;
  bonus_value_cad: number | null;
  qualifying_spend_cad: number | null;
  discount_percent: number | null;
  points_amount: number | null;
  points_program_name: string | null;
  flyer_deeplink_url: string | null;
  flyer_page_number: number | null;
  dedupe_key: string;
  batch_run_id?: string;
  review_status?: string | null;
  // CR-Corporate
  eligibility_scope?: string | null;
  audience_hint?: string | null;
  // CR151: Policy proof fields
  policy_config_id?: string | null;
  policy_version?: string | null;
  policy_loaded_at?: string | null;
  eligibility_scope_reason?: string | null;
  eligibility_scope_rule_ids_fired?: string[] | null;
  eligibility_scope_evidence_snippets?: string[] | null;
  // CR332: Flyer artifact
  flyer_artifact?: FlyerArtifact | null;
  // CR333: Per-deal-type flyer evidence toggle
  show_flyer_evidence?: boolean;
  // CR-Issuer: Resolved issuer fields
  issuer_display_name?: string | null;
  issuer_logo_url?: string | null;
  issuer_merchant_key?: string | null;
  // CR416: Server-computed reward fields
  reward_label?: string | null;
  reward_value_primary?: string | null;
  reward_value_secondary?: string | null;
  reward_source?: string | null;
  // CR417: Server-computed header brand fields
  header_brand_name?: string | null;
  header_brand_source?: string | null;
  header_context_merchant_name?: string | null;
  header_context_merchant_source?: string | null;
  // CR417: promo_merchant_name
  promo_merchant_name?: string | null;
}

interface SnapshotInfo {
  snapshot_id: string | null;
  batch_run_id: string | null;
  published_at: string | null;
  source_table?: string;
}

export interface PreviewDeal extends Deal {
  id: string;
  dedupe_key: string;
  review_status: string | null;
  // CR279: Variant-level purchase brand used by card title fallback in preview surfaces
  nested_purchase_brand?: string | null;
  // CR151: Policy proof fields
  policy_config_id: string | null;
  policy_version: string | null;
  policy_loaded_at: string | null;
  eligibility_scope_reason: string | null;
  eligibility_scope_rule_ids_fired: string[] | null;
  eligibility_scope_evidence_snippets: string[] | null;
  // CR332: Flyer artifact
  flyer_artifact?: FlyerArtifact | null;
  // CR333: Per-deal-type flyer evidence toggle
  show_flyer_evidence?: boolean;
  // CR-Issuer: Resolved issuer fields
  issuer_display_name?: string | null;
  issuer_logo_url?: string | null;
  issuer_merchant_key?: string | null;
  // CR416: Server-computed reward fields
  reward_label?: string | null;
  reward_value_primary?: string | null;
  reward_value_secondary?: string | null;
  reward_source?: string | null;
  // CR417: Server-computed header brand fields
  header_brand_name?: string | null;
  header_brand_source?: string | null;
  header_context_merchant_name?: string | null;
  header_context_merchant_source?: string | null;
}

/**
 * CR098: Map API response to UI Deal type with extra fields for staging.
 * 
 * IMPORTANT: This mapping must preserve all deal_type information so that
 * getDealType() can correctly resolve the category. We set BOTH:
 *   - deal_category (uppercase enum for direct use)
 *   - deal_type (lowercase for fallback)
 */
function mapPreviewDealToUIDeal(d: AdminPreviewDealResponse): PreviewDeal {
  // Normalize deal_type to proper category
  const rawType = d.deal_type?.toLowerCase() || "bonus";
  const upperType = d.deal_type?.toUpperCase().replace(/-/g, "_") || "BONUS";
  const validCategories = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];
  const dealCategory = validCategories.includes(upperType) 
    ? (upperType as DealCategory) 
    : "UNCLASSIFIED";

  // CR277: Look up loyalty program for points deals
  const loyaltyProgram = d.points_program_name ? lookupByProgramName(d.points_program_name) : null;
  const pointsProgramId = loyaltyProgram?.programId || null;
  const pointsConversionRate = loyaltyProgram?.conversionRate || null;
  const pointsCashValue = d.points_amount && loyaltyProgram
    ? calculatePointsCashValue(d.points_amount, d.points_program_name)
    : null;

  return {
    id: d.id,
    dedupe_key: d.dedupe_key,
    review_status: d.review_status || null,
    
    // CR151: Policy proof fields
    policy_config_id: d.policy_config_id || null,
    policy_version: d.policy_version || null,
    policy_loaded_at: d.policy_loaded_at || null,
    eligibility_scope_reason: d.eligibility_scope_reason || null,
    eligibility_scope_rule_ids_fired: d.eligibility_scope_rule_ids_fired || null,
    eligibility_scope_evidence_snippets: d.eligibility_scope_evidence_snippets || null,
    
    merchant: d.merchant_name,
    // CR098: Set BOTH category and type for getDealType() fallback chain
    deal_category: dealCategory,
    deal_type: rawType as "bonus" | "discount" | "bundle" | "points" | "in_kind" | "unclassified",
    headline_mechanic: d.headline_mechanic || "",
    
    purchase_gift_card_brand: d.purchase_gift_card_brand || null,
    nested_purchase_brand: d.nested_purchase_brand || null,
    qualifying_product_name: d.qualifying_product_name || d.nested_purchase_brand || null,
    purchase_brand_logo_url: null,
    
    bonus_merchant: d.bonus_merchant || null,
    bonus_merchant_logo_url: null,
    bonus_amount: d.bonus_value_cad || null,
    spend_amount: d.qualifying_spend_cad || null,
    bonus_type: undefined,
    qualifying_spend_cad: d.qualifying_spend_cad || null,
    bonus_value_cad: d.bonus_value_cad || null,
    
    gift_card_brand: d.purchase_gift_card_brand || null,
    bonus_or_discount_amount: null,
    
    discount_subtype: undefined,
    pay_amount_cad: null,
    receive_value_cad: null,
    discount_amount_cad: null,
    min_purchase_cad: null,
    
    spend_range_min: null,
    spend_range_max: null,
    discount_percent: d.discount_percent || null,
    discount_tiers: null,
    deal_pricing_type: null,
    
    // Points fields - CR277: Use loyalty program lookup
    points_amount: d.points_amount || null,
    points_program_id: pointsProgramId,
    points_program_name: d.points_program_name || null,
    points_to_currency_rate: pointsConversionRate,
    
    in_kind_tiers: null,
    
    valuation_source: undefined,
    unit_value_cad: null,
    valuation_reference_url: null,
    valuation_notes: null,
    
    // CR277: Use points cash value for POINTS deals
    computed_value_cad: dealCategory === "POINTS" && pointsCashValue 
      ? pointsCashValue.cashValue 
      : (d.computed_value_cad || null),
    effective_percent_back: dealCategory === "POINTS" && pointsCashValue && d.qualifying_spend_cad
      ? (pointsCashValue.cashValue / d.qualifying_spend_cad) * 100
      : (d.effective_percent_back || null),
    normalized_value_score: null,
    
    start_date: d.valid_from || null,
    end_date: d.valid_until || null,
    bonus_valid_from: null,
    bonus_valid_until: null,
    purchase_window_start: null,
    purchase_window_end: null,
    redeem_window_start: null,
    redeem_window_end: null,
    claim_deadline: null,
    
    source_url: d.source_url || null,
    source_type: (d.source_type as any) || "html",
    flyer_deeplink_url: d.flyer_deeplink_url || null,
    flyer_page_number: d.flyer_page_number || null,
    flyer_page_position: null,
    raw_source_notes: "",
    
    claim_url: d.claim_url || null,
    claim_channel: (d as any).claim_channel || null,
    
    fine_print: "",
    exclusions: null,
    disclaimer_required: false,
    disclaimer_text: null,
    requires_physical_card: false,
    proof_urls: d.source_url ? [d.source_url] : [],
    confidence: "High" as const,
    confidence_reason: "",
    region: "ON",
    channel: "unknown",
    availability_scope: (d.availability_scope as any) || "unknown",
    availability_notes: d.availability_notes || null,
    eligibility_scope: (d.eligibility_scope as any) || undefined,
    audience_hint: (d.audience_hint as any) || undefined,
    redeem_url: null,
    campaign_name: null,
    // CR332: Pass through flyer artifact
    flyer_artifact: d.flyer_artifact ?? null,
    // CR333: Per-deal-type flyer evidence toggle
    show_flyer_evidence: d.show_flyer_evidence ?? true,
    // CR-Issuer: Resolved issuer fields
    issuer_display_name: d.issuer_display_name ?? null,
    issuer_logo_url: d.issuer_logo_url ?? null,
    issuer_merchant_key: d.issuer_merchant_key ?? null,
    // CR416: Server-computed reward fields (pass through unchanged)
    reward_label: d.reward_label ?? null,
    reward_value_primary: d.reward_value_primary ?? null,
    reward_value_secondary: d.reward_value_secondary ?? null,
    reward_source: d.reward_source ?? null,
    // CR417: Server-computed header brand fields (pass through unchanged)
    header_brand_name: d.header_brand_name ?? null,
    header_brand_source: d.header_brand_source ?? null,
    header_context_merchant_name: d.header_context_merchant_name ?? null,
    header_context_merchant_source: d.header_context_merchant_source ?? null,
    // CR417: promo_merchant_name
    promo_merchant_name: d.promo_merchant_name ?? undefined,
  };
}

export interface BatchRunOption {
  id: string;
  created_at: string;
  status: string;
  total_staged_items: number | null;
  deduped_count: number | null;
  merchants_in_scope: number | null;
}

export interface UseAdminPreviewDealsResult {
  deals: PreviewDeal[];
  snapshot: SnapshotInfo | null;
  mode: PreviewMode;
  loading: boolean;
  error: string | null;
  batchRuns: BatchRunOption[];
  batchRunsLoading: boolean;
  fetchDeals: (mode: PreviewMode, batchRunId?: string) => Promise<void>;
  fetchBatchRuns: () => Promise<void>;
  submitReview: (dealId: string, decision: "ACCEPTED" | "REJECTED" | "PENDING", batchRunId: string, dedupeKey?: string, note?: string) => Promise<boolean>;
}

/**
 * Hook to fetch admin preview deals from staging or production datasets.
 * Supports three modes: staging, admin (internal prod), and live (public).
 */
export function useAdminPreviewDeals(): UseAdminPreviewDealsResult {
  const [deals, setDeals] = useState<PreviewDeal[]>([]);
  const [snapshot, setSnapshot] = useState<SnapshotInfo | null>(null);
  const [mode, setMode] = useState<PreviewMode>("live");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batchRuns, setBatchRuns] = useState<BatchRunOption[]>([]);
  const [batchRunsLoading, setBatchRunsLoading] = useState(false);

  const fetchBatchRuns = useCallback(async () => {
    setBatchRunsLoading(true);
    try {
      const { data, error: queryError } = await supabase
        .from("cr075_batch_runs")
        .select("id, created_at, status, total_staged_items, deduped_count, merchants_in_scope")
        .eq("is_deleted", false)
        .order("created_at", { ascending: false })
        .limit(25);

      if (queryError) {
        console.error("[useAdminPreviewDeals] Batch runs query error:", queryError);
        return;
      }

      setBatchRuns(data || []);
    } catch (err) {
      console.error("[useAdminPreviewDeals] Batch runs fetch error:", err);
    } finally {
      setBatchRunsLoading(false);
    }
  }, []);

  const fetchDeals = useCallback(async (fetchMode: PreviewMode = "live", batchRunId?: string) => {
    setLoading(true);
    setError(null);
    setMode(fetchMode);

    try {
      const params = new URLSearchParams({ mode: fetchMode });
      if (fetchMode === "staging" && batchRunId) {
        params.append("batch_run_id", batchRunId);
      }

      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError("Authentication required");
        setLoading(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke(
        `admin-preview-deals?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );

      if (fnError) {
        console.error("[useAdminPreviewDeals] Function error:", fnError);
        setError(fnError.message);
        return;
      }

      if (data.error) {
        console.error("[useAdminPreviewDeals] API error:", data.error);
        setError(data.error);
        return;
      }

      // Map deals to UI format
      const mappedDeals = (data.deals || []).map(mapPreviewDealToUIDeal);
      
      // CR437: For admin/live modes, hydrate deals through bulk render payload RPC
      // This ensures Live Preview renders from the same SSOT contract as Deal Cards preview
      if (fetchMode !== "staging" && mappedDeals.length > 0) {
        try {
          const dealIds = mappedDeals.map((d: PreviewDeal) => d.id).filter(Boolean);
          console.log(`[CR437] Hydrating ${dealIds.length} deals via bulk render payload RPC`);
          
          const { data: rpcData, error: rpcError } = await supabase.rpc(
            "get_deal_card_render_payloads" as any,
            {
              p_environment: fetchMode === "admin" ? "preview" : "production",
              p_deal_ids: dealIds,
            }
          );

          if (rpcError) {
            console.warn("[CR437] Bulk render payload RPC failed, using edge function data:", rpcError.message);
          } else {
            const rows = Array.isArray(rpcData) ? rpcData : [];
            const payloadMap = new Map<string, Record<string, unknown>>();
            
            for (const row of rows) {
              const meta = row.meta as any;
              if (row.deal_json && meta?.found && meta?.hoisted === true) {
                payloadMap.set(row.deal_id as string, row.deal_json as Record<string, unknown>);
              } else if (meta?.hoisted !== true && meta?.found) {
                console.warn(`[CR437] Contract violation for deal ${row.deal_id}: hoisted !== true`);
              }
            }

            // Overlay render payload fields onto mapped deals
            for (const deal of mappedDeals) {
              const payload = payloadMap.get(deal.id);
              if (payload) {
                // Overlay key contract fields from SSOT payload
                deal.computed_value_cad = (payload.computed_value_cad as number) ?? deal.computed_value_cad;
                deal.effective_percent_back = (payload.effective_percent_back as number) ?? deal.effective_percent_back;
                deal.qualifying_spend_cad = (payload.qualifying_spend_cad as number) ?? deal.qualifying_spend_cad;
                deal.discount_amount_cad = (payload.discount_amount_cad as number) ?? deal.discount_amount_cad;
                deal.discount_percent = (payload.discount_percent as number) ?? deal.discount_percent;
                deal.start_date = (payload.start_date as string) ?? deal.start_date;
                deal.end_date = (payload.end_date as string) ?? deal.end_date;
                deal.bonus_value_cad = ((payload.value as any)?.bonus_value_cad as number) ?? deal.bonus_value_cad;
                // CR437: Attach the full canonical_deal_json from the contract
                (deal as any).canonical_deal_json = payload.canonical_deal_json ?? payload;
                // CR437: Mark as contract-hydrated
                (deal as any)._contract_hydrated = true;
              }
            }

            console.log(`[CR437] Hydrated ${payloadMap.size}/${dealIds.length} deals from SSOT contract`);
          }
        } catch (hydrateErr) {
          console.warn("[CR437] Hydration failed, falling back to edge function data:", hydrateErr);
        }
      }

      // CR447: Batch hydrate flyer artifacts for all surfaces
      const hydratedDeals = await hydrateFlyerArtifactsBatch(mappedDeals);
      setDeals(hydratedDeals as typeof mappedDeals);
      setSnapshot(data.snapshot || null);

      console.log(`[useAdminPreviewDeals] Fetched ${mappedDeals.length} deals for mode=${fetchMode}`);
    } catch (err) {
      console.error("[useAdminPreviewDeals] Unexpected error:", err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const submitReview = useCallback(async (
    dealId: string, 
    decision: "ACCEPTED" | "REJECTED" | "PENDING", 
    batchRunId: string,
    dedupeKey?: string,
    note?: string
  ): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.error("[useAdminPreviewDeals] No session for review");
        return false;
      }

      const { data, error: fnError } = await supabase.functions.invoke("staging-review", {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          batch_run_id: batchRunId,
          deal_id: dealId,
          decision,
          dedupe_key: dedupeKey,
          note,
        },
      });

      if (fnError || data?.error) {
        console.error("[useAdminPreviewDeals] Review error:", fnError || data?.error);
        return false;
      }

      // Update local state
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? { ...deal, review_status: decision } : deal
      ));

      return true;
    } catch (err) {
      console.error("[useAdminPreviewDeals] Review submit error:", err);
      return false;
    }
  }, []);

  return {
    deals,
    snapshot,
    mode,
    loading,
    error,
    batchRuns,
    batchRunsLoading,
    fetchDeals,
    fetchBatchRuns,
    submitReview,
  };
}
